/**
 * PanelErrorBoundary — one boundary per Models & Routing panel.
 *
 * The brief: "Each panel has its own error boundary. A failure shows a
 * plain message and a retry in that panel only." A render-time crash in
 * one panel can never take the settings page — or another panel — down
 * with it.
 *
 * Also exports the small read-state chrome the panels share
 * (RouterReadLoading / RouterReadError) so loading and failure look the
 * same in every panel.
 */

import React, { type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface BoundaryProps {
  /** Human panel name, shown in the message ("Models", "Routing", …). */
  panelLabel: string;
  children: ReactNode;
}

interface BoundaryState {
  error: Error | null;
  /** Bumped on retry to remount the failed subtree. */
  resetKey: number;
}

export class PanelErrorBoundary extends React.Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null, resetKey: 0 };

  static getDerivedStateFromError(error: Error): Partial<BoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error): void {
    // Keep the console trail without taking any further action — the panel
    // is already isolated by the boundary.
    console.error(`[settings] panel "${this.props.panelLabel}" failed to render`, error);
  }

  private handleRetry = (): void => {
    this.setState((state) => ({ error: null, resetKey: state.resetKey + 1 }));
  };

  render(): ReactNode {
    const { error, resetKey } = this.state;
    if (error) {
      return (
        <div
          data-testid="panel-error-boundary"
          role="alert"
          style={{
            border: '1px solid var(--sp-border)',
            borderRadius: 'var(--sp-radius-lg)',
            background: 'var(--sp-bg-elev)',
            padding: 'var(--sp-space-4)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-space-2)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--sp-error)' }}>
            <AlertTriangle size={15} />
            <span style={{ fontWeight: 600, fontSize: 13 }}>
              {this.props.panelLabel} could not render.
            </span>
          </div>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--sp-text-muted)' }}>
            The rest of settings is unaffected. You can retry this panel without reloading the page.
          </p>
          <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)' }}>{error.message}</div>
          <div>
            <button
              type="button"
              className="sp-btn"
              onClick={this.handleRetry}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <RefreshCw size={13} />
              Try again
            </button>
          </div>
        </div>
      );
    }
    return <React.Fragment key={resetKey}>{this.props.children}</React.Fragment>;
  }
}

/** Shared loading placeholder for a panel's read. */
export const RouterReadLoading: React.FC<{ label?: string }> = ({ label }) => (
  <div
    data-testid="router-read-loading"
    style={{ padding: 'var(--sp-space-4)', fontSize: 12, color: 'var(--sp-text-dim)' }}
  >
    {label ?? 'Loading…'}
  </div>
);

/** Shared in-panel read failure: plain message, retry in this panel only. */
export const RouterReadError: React.FC<{ error: Error; onRetry: () => void }> = ({ error, onRetry }) => (
  <div
    data-testid="router-read-error"
    role="alert"
    style={{
      border: '1px solid var(--sp-border)',
      borderRadius: 'var(--sp-radius-lg)',
      background: 'var(--sp-bg-elev)',
      padding: 'var(--sp-space-4)',
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--sp-space-2)',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--sp-error)' }}>
      <AlertTriangle size={15} />
      <span style={{ fontWeight: 600, fontSize: 13 }}>This panel could not load its data.</span>
    </div>
    <p style={{ margin: 0, fontSize: 12, color: 'var(--sp-text-muted)' }}>{error.message}</p>
    <div>
      <button
        type="button"
        className="sp-btn"
        onClick={onRetry}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
      >
        <RefreshCw size={13} />
        Retry
      </button>
    </div>
  </div>
);

/** Greyed "Coming soon" block for controls the engine does not support yet.
 *  Visible and honest (the brief's rule 4): never pretends to work. */
export const ComingSoonBlock: React.FC<{ title: string; body: string; testId?: string }> = ({
  title,
  body,
  testId,
}) => (
  <div
    data-testid={testId}
    aria-disabled="true"
    style={{
      border: '1px dashed var(--sp-border)',
      borderRadius: 'var(--sp-radius-lg)',
      padding: 'var(--sp-space-3) var(--sp-space-4)',
      opacity: 0.6,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--sp-text)' }}>{title}</span>
      <span
        className="mono"
        style={{
          fontSize: 10,
          color: 'var(--sp-text-dim)',
          border: '1px solid var(--sp-border)',
          borderRadius: 999,
          padding: '2px 8px',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
        }}
      >
        Coming soon
      </span>
    </div>
    <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--sp-text-muted)' }}>{body}</p>
  </div>
);
